import { useEffect, useMemo, useRef } from 'react';
import type { FrameRegion } from '@repo/ui';

import type { Polygon } from '../../components/PolygonPlotter2/PolygonPlotter2';
import { zoomRegionsByCamera } from './observerSessionState';
import type { ObserverStateByRefid } from './observerSessionState';

/**
 * `zoomRegionsByCamera`, held across renders so an unchanged camera keeps its array
 * (`camera-zoom-dwell-and-observers`, FR8/NFR1) — the one place both stages take their zoom regions
 * from, so the identity reuse the camera wall's memo depends on cannot be wired two ways.
 *
 * The previous map is READ during render and WRITTEN after commit: a discarded concurrent render
 * then costs at most an allocation, never a wrong region, because the builder reuses a previous array
 * only when every entry's `id` and `points` match.
 */
export const useZoomRegionsByCamera = (
  polygonsByEntity: Record<string, Polygon[]>,
  observerState: ObserverStateByRefid,
): ReadonlyMap<number, readonly FrameRegion[]> => {
  const previous = useRef<ReadonlyMap<number, readonly FrameRegion[]> | undefined>(undefined);
  const map = useMemo(
    () => zoomRegionsByCamera(polygonsByEntity, observerState, previous.current),
    [polygonsByEntity, observerState],
  );
  useEffect(() => {
    previous.current = map;
  }, [map]);
  return map;
};
